import * as React from 'react';
// @ts-expect-error
import type {Control, Watch, Append, Move, Update} from 'react-hook-form';

import {useMutation} from '@apollo/react-hooks';

import {DraggableList} from 'shells/draggable-row/draggable-list';
import {FlashBanner} from 'shells/flash-banner/flash-banner';

import type {
    CreateField as CreateFieldMutation,
    CreateFieldVariables as CreateFieldMutationVariables,
    FieldCreateInput,
} from 'nutshell-graphql-types';

import CREATE_FIELD from '../../../graphql/mutations/create-field.graphql';
import {convertGraphQLResponseToFormFieldState, getInputStyles} from '../../../helpers';
import type {ActiveField, FormFieldRowForDuplicateHelpers} from '../../../types';

import type {FormFieldSchema} from '../../../../core/nutshell-fields/types';

import {FONT_OPTIONS} from '../fonts/constants';
import {fetchFormFont} from '../fonts/form-font-helpers';
import {updateExistingPersonEmailFields} from '../helpers';

import {AddBlock} from './add-card/add-block';
import {AddField} from './add-card/add-field';
import {SubmitButtonField} from './fields/submit-button-field';
import {getFieldComponents} from './get-field-components';
import {LoadingField} from './loading-field';

import styles from './editor.module.css';

type Props = {
    formId: string;
    formHeader?: React.ReactNode;
    fields: FormFieldRowForDuplicateHelpers[];
    watch: Watch;
    control: Control;
    append: Append;
    update: Update;
    onRemoveField: (index: number) => void;
    move: Move;
    setActiveField: (arg1?: ActiveField | null | undefined) => void;
    activeField: ActiveField | null | undefined;
    postAddField: () => Promise<any>;
    saveFailedErrorMessage: string | null | undefined;
    onClearError: () => void;
    formSchema: FormFieldSchema;
    fetchFormSchema: () => void;
    disableHeaderFields?: boolean;
    minimumOneEmailField?: boolean;
};

export function Editor(props: Props) {
    const [isCreatingField, setIsCreatingField] = React.useState<boolean>(false);

    const backgroundColor = props.watch('backgroundColor');
    const fontStyle = props.watch('fontStyle');
    const fontFamily = fontStyle ? fontStyle.fontFamily : undefined;
    const borderRadius = props.watch('inputStyle.borderRadius');
    const border = props.watch('inputStyle.border');
    const fontSize = props.watch('inputStyle.fontSize');
    const color = props.watch('inputStyle.color');

    React.useEffect(() => {
        FONT_OPTIONS.forEach((option) => fetchFormFont(option.value.name));
    }, []);

    const [createField] = useMutation<CreateFieldMutation, CreateFieldMutationVariables>(
        CREATE_FIELD
    );

    const submitButtonId = props.watch('submitButton.id');

    const handleSetActiveField = (newField?: ActiveField | null) => {
        props.setActiveField(newField);
    };

    const fieldComponents = getFieldComponents({
        fields: props.fields,
        formSchema: props.formSchema,
        activeField: props.activeField,
        setActiveField: handleSetActiveField,
        watch: props.watch,
        control: props.control,
        onRemoveField: props.onRemoveField,
    });

    const createNewField = (newField: FieldCreateInput) => {
        setIsCreatingField(true);
        createField({
            variables: {
                input: newField,
            },
        }).then((data) => {
            const createdField =
                data && data.data && data.data.fieldCreate && data.data.fieldCreate.field
                    ? data.data.fieldCreate.field
                    : undefined;

            if (createdField) {
                props.append(convertGraphQLResponseToFormFieldState(createdField));
                setIsCreatingField(false);

                // if the form requires one or more email addresses and this addition results
                // in more than one email field, we mark both as removable to allow deletion or
                // mapping to a different entity
                if (props.minimumOneEmailField && newField.fieldName === 'email') {
                    updateExistingPersonEmailFields(props.fields, props.update);
                }
            }

            props.postAddField();
        });
    };

    return (
        <div style={{backgroundColor}} className={styles['outer-container']}>
            {props.saveFailedErrorMessage ? (
                <FlashBanner
                    type='error'
                    hasBorder={true}
                    leftAlignContent={true}
                    onHidden={props.onClearError}
                >
                    {props.saveFailedErrorMessage}
                </FlashBanner>
            ) : undefined}
            <div
                onClick={() => {
                    props.setActiveField(null);
                }}
                className={styles['inner-container']}
            >
                <div className={styles['fields-container']}>
                    <div className={styles['header-container']}>{props.formHeader}</div>
                    <div onClick={(e) => e.stopPropagation()}>
                        <div style={{fontFamily}} className={styles['draggable-list-container']}>
                            <DraggableList
                                listItems={fieldComponents}
                                handleReorder={props.move}
                                showDraggableRowOnlyOnHover={true}
                            />
                            {isCreatingField ? (
                                <div className={styles.field}>
                                    <LoadingField
                                        fontSize={fontSize}
                                        placeholderStyles={getInputStyles({
                                            borderRadius,
                                            border,
                                            fontSize,
                                            color,
                                        })}
                                    />
                                </div>
                            ) : undefined}
                        </div>
                        {props.disableHeaderFields ? (
                            <AddField
                                formId={props.formId}
                                fields={props.fields}
                                onAddCardClick={() => {
                                    props.setActiveField(null);
                                }}
                                onCreateNewField={createNewField}
                                formSchema={props.formSchema}
                                fetchFormSchema={props.fetchFormSchema}
                            />
                        ) : (
                            <AddBlock
                                formId={props.formId}
                                fields={props.fields}
                                onAddCardClick={() => {
                                    props.setActiveField(null);
                                }}
                                onCreateNewField={createNewField}
                                formSchema={props.formSchema}
                                fetchFormSchema={props.fetchFormSchema}
                            />
                        )}
                        {submitButtonId ? (
                            <div
                                style={{fontFamily}}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.setActiveField({id: submitButtonId});
                                }}
                                className={styles['submit-container']}
                            >
                                <SubmitButtonField
                                    control={props.control}
                                    name='submitButton'
                                    watch={props.watch}
                                />
                            </div>
                        ) : undefined}
                    </div>
                </div>
            </div>
        </div>
    );
}
